import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  accordionSummaryClasses,
} from "@mui/material";
import {
  CARD_FEES,
  type CardFee,
} from "Components/Merchant/FeeCalculator/constants";
import { Resources, useResource } from "Translations/Resources";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import {
  type MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useState } from "react";

type Props = {
  currentAcquiringFee: number;
  proposedAcquiringFee: number;
};

export const CardRatesTable = ({
  currentAcquiringFee,
  proposedAcquiringFee,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const localization = useTableLocalization();
  const { t } = useResource();

  const columns: Array<MRT_ColumnDef<CardFee>> = [
    {
      accessorKey: "cardType",
      header: t(
        Resources.Pages.Content.MerchantFeeCalculator.CardRatesTable.Headers
          .CardType,
      ),
    },
    {
      accessorKey: "interchange",
      header: t(
        Resources.Pages.Content.MerchantFeeCalculator.CardRatesTable.Headers
          .Interchange,
      ),
      Cell: ({ row }) => `${row.original.interchange}%`,
    },
    {
      accessorKey: "schemeFee",
      header: t(
        Resources.Pages.Content.MerchantFeeCalculator.CardRatesTable.Headers
          .SchemeFee,
      ),
      Cell: ({ row }) => `${row.original.schemeFee}%`,
    },
    {
      accessorKey: "currentTotalRate",
      header: t(
        Resources.Pages.Content.MerchantFeeCalculator.CardRatesTable.Headers
          .CurrentTotalRate,
      ),
      Cell: ({ row }) =>
        `${(row.original.interchange + row.original.schemeFee + currentAcquiringFee).toFixed(3)}%`,
    },
    {
      accessorKey: "proposedTotalRate",
      header: t(
        Resources.Pages.Content.MerchantFeeCalculator.CardRatesTable.Headers
          .ProposedTotalRate,
      ),
      Cell: ({ row }) =>
        `${(row.original.interchange + row.original.schemeFee + proposedAcquiringFee).toFixed(3)}%`,
    },
  ];

  const table = useMaterialReactTable({
    data: CARD_FEES,
    columns,
    localization,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    muiTablePaperProps: {
      sx: {
        borderRadius: "10px",
      },
    },
  });

  return (
    <Accordion
      disableGutters
      sx={{ background: "transparent", boxShadow: "none" }}
      onChange={(_, expanded) => setIsExpanded(expanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="card-rates-content"
        id="card-rates-header"
        sx={{
          flexDirection: "row-reverse",
          [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
            { transform: "rotate(180deg)" },
          [`& .${accordionSummaryClasses.content}`]: { flexGrow: 0 },
        }}
      >
        <Typography fontSize="1rem">
          {isExpanded
            ? t(
                Resources.Pages.Content.MerchantFeeCalculator.CardRatesTable
                  .Collapse,
              )
            : t(
                Resources.Pages.Content.MerchantFeeCalculator.CardRatesTable
                  .Expand,
              )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <MaterialReactTable table={table} />
      </AccordionDetails>
    </Accordion>
  );
};
